if (!customElements.get('collection-load-more')) {
  customElements.define('collection-load-more', class CollectionLoadMore extends HTMLElement {
    connectedCallback() {
      this.grid = this.closest('#ProductGridContainer')?.querySelector('#product-grid');
      this.button = this.querySelector('[data-load-more-button]');
      this.status = this.querySelector('[data-load-more-status]');
      this.error = this.querySelector('[data-load-more-error]');
      this.fallback = this.querySelector('[data-load-more-fallback]');
      if (!this.grid || !this.button || !window.fetch || !window.AbortController) return;
      this.label = this.button.textContent.trim();
      this.suspended = false;
      this.onClick = () => this.load();
      this.onResultsChanging = () => {
        this.suspended = true;
        this.scrollObserver?.disconnect();
        this.cancel();
        this.button.disabled = true;
        this.error.hidden = true;
        this.status.textContent = '';
      };
      this.button.addEventListener('click', this.onClick);
      document.addEventListener('collection:results-changing', this.onResultsChanging);
      this.querySelector('[data-load-more-pagination]').hidden = true;
      this.button.hidden = !this.dataset.nextUrl;
      if (this.dataset.loadMoreType === 'scroll' && window.IntersectionObserver) {
        this.scrollObserver = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting) && this.error.hidden) this.load();
        }, { rootMargin: '0px 0px 200px 0px' });
        if (this.dataset.nextUrl) this.scrollObserver.observe(this);
      }
    }

    disconnectedCallback() {
      this.scrollObserver?.disconnect();
      this.cancel();
      this.button?.removeEventListener('click', this.onClick);
      document.removeEventListener('collection:results-changing', this.onResultsChanging);
    }

    cancel() {
      this.request?.abort();
      this.request = null;
      clearTimeout(this.timer);
      this.grid?.removeAttribute('aria-busy');
    }

    pageUrl(value) {
      const url = new URL(value, window.location.href);
      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) {
        throw new Error('Unexpected collection URL');
      }
      url.searchParams.delete('section_id');
      return url;
    }

    async load() {
      if (this.request || this.suspended || !this.dataset.nextUrl) return;
      this.scrollObserver?.disconnect();
      const controller = new AbortController();
      this.request = controller;
      let timedOut = false;
      this.timer = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, 15000);
      this.button.disabled = true;
      this.button.textContent = this.dataset.loading;
      this.status.textContent = this.dataset.loading;
      this.error.hidden = true;
      this.grid.setAttribute('aria-busy', 'true');

      try {
        const url = this.pageUrl(this.dataset.nextUrl);
        url.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error('Collection request failed');
        const html = await response.text();
        if (timedOut) throw new Error('Collection request timed out');
        if (this.request !== controller || this.suspended || !this.isConnected) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const nextGrid = doc.getElementById('product-grid');
        const nextControl = doc.querySelector('collection-load-more');
        const nextPage = Number(nextGrid?.dataset.page);
        if (!nextGrid || nextGrid.dataset.id !== this.dataset.sectionId ||
            nextPage !== Number(this.grid.dataset.page) + 1 ||
            !nextControl || nextControl.dataset.sectionId !== this.dataset.sectionId ||
            !nextControl.hasAttribute('data-next-url')) {
          throw new Error('Invalid collection response');
        }
        const nextUrl = nextControl.dataset.nextUrl ? this.pageUrl(nextControl.dataset.nextUrl).href : '';
        if (nextUrl && nextUrl === this.pageUrl(this.dataset.nextUrl).href) {
          throw new Error('Pagination did not advance');
        }
        const seen = new Set(Array.from(this.grid.querySelectorAll('[data-collection-product-id]'), el => el.dataset.collectionProductId));
        const cards = Array.from(nextGrid.children).filter(el => {
          const id = el.dataset.collectionProductId;
          if (!id || seen.has(id)) return false;
          seen.add(id);
          return true;
        });
        if (!cards.length) throw new Error('No new products in response');
        cards.forEach(card => {
          card.querySelectorAll('.scroll-trigger').forEach(el => el.classList.add('scroll-trigger--cancel'));
          card.classList.add('scroll-trigger--cancel');
        });
        const fragment = document.createDocumentFragment();
        cards.forEach(card => fragment.append(card));
        const moveFocus = document.activeElement === this.button;
        this.grid.append(fragment);
        this.grid.dataset.page = String(nextPage);
        this.dataset.nextUrl = nextUrl;
        this.fallback.href = nextUrl;
        this.button.hidden = !nextUrl;
        this.status.textContent = nextUrl ? this.dataset.loaded : this.dataset.complete;
        if (moveFocus) {
          const firstLink = cards[0].querySelector('a[href]');
          firstLink?.focus({ preventScroll: true });
        }
      } catch (error) {
        if (this.request !== controller || this.suspended || !this.isConnected) return;
        this.error.hidden = false;
        this.status.textContent = '';
        this.button.textContent = this.dataset.retry;
      } finally {
        if (this.request === controller) {
          clearTimeout(this.timer);
          this.request = null;
          this.grid.removeAttribute('aria-busy');
          this.button.disabled = false;
          if (this.error.hidden) this.button.textContent = this.label;
          if (!this.suspended && this.isConnected && this.dataset.nextUrl && this.error.hidden) {
            this.scrollObserver?.observe(this);
          }
        }
      }
    }
  });
}
